// contexts/ReloadContext.js - ReactJS Web Version
import React, { createContext, useState, useContext, useCallback, useRef, useEffect } from 'react';

const ReloadContext = createContext();

export const useReload = () => {
  const context = useContext(ReloadContext);
  if (!context) {
    throw new Error('useReload must be used within ReloadProvider');
  }
  return context;
};

export const ReloadProvider = ({ children }) => {
  const [reloadKey, setReloadKey] = useState(0);
  const [isReloading, setIsReloading] = useState(false);
  const reloadTimeoutRef = useRef(null);
  const documentVisibilityRef = useRef(document.visibilityState);

  // Soft reload (remounts components without full app restart)
  const softReload = useCallback(() => {
    console.log('🔄 Performing soft reload...');
    setReloadKey(prev => {
      const newKey = prev + 1;
      console.log(`📊 Reload key updated: ${prev} -> ${newKey}`);
      return newKey;
    });
    return true;
  }, []);

  // Hard reload (full page refresh)
  const hardReload = useCallback(async () => {
    if (isReloading) {
      console.log('⚠️ Reload already in progress');
      return false;
    }

    console.log('🔄 Performing hard reload...');
    setIsReloading(true);

    try {
      // For web, hard reload means refreshing the page
      window.location.reload();
      return true;
    } catch (error) {
      console.error('❌ Hard reload failed:', error);
      // Fallback to soft reload
      softReload();
      return false;
    } finally {
      setIsReloading(false);
    }
  }, [isReloading, softReload]);

  // Smart reload - chooses best method based on what needs to be reloaded
  const smartReload = useCallback(async (options = {}) => {
    const {
      forceHard = false,
      delay = 0,
      onBeforeReload = null,
      onAfterReload = null
    } = options;

    if (onBeforeReload) {
      await onBeforeReload();
    }

    const performReload = async () => {
      let success = false;
      
      if (forceHard) {
        success = await hardReload();
      } else {
        success = softReload();
      }
      
      if (onAfterReload && success) {
        setTimeout(() => {
          onAfterReload();
        }, 100);
      }
      
      return success;
    };

    if (delay > 0) {
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }
      
      return new Promise((resolve) => {
        reloadTimeoutRef.current = setTimeout(async () => {
          const result = await performReload();
          resolve(result);
        }, delay);
      });
    } else {
      return await performReload();
    }
  }, [hardReload, softReload]);

  // Reload specific component by key
  const reloadComponent = useCallback((componentKey) => {
    console.log(`🔄 Reloading component: ${componentKey}`);
    softReload();
  }, [softReload]);

  // Clear any pending reloads
  const clearPendingReload = useCallback(() => {
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
      reloadTimeoutRef.current = null;
    }
  }, []);

  // Listen for page visibility changes (tab focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const currentVisibility = document.visibilityState;
      
      // When tab becomes visible again
      if (documentVisibilityRef.current === 'hidden' && currentVisibility === 'visible') {
        console.log('📱 Tab became visible, performing soft reload');
        softReload();
      }
      
      documentVisibilityRef.current = currentVisibility;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [softReload]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Connection restored, performing soft reload');
      softReload();
    };

    const handleOffline = () => {
      console.log('🌐 Connection lost');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [softReload]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }
    };
  }, []);

  const value = {
    reloadKey,
    isReloading,
    softReload,
    hardReload,
    smartReload,
    reloadComponent,
    clearPendingReload,
  };

  return (
    <ReloadContext.Provider value={value}>
      {children}
    </ReloadContext.Provider>
  );
};

// HOC to add reload capability to any component
export const withReload = (WrappedComponent) => {
  return (props) => {
    const reloadContext = useReload();
    return <WrappedComponent {...props} reloadContext={reloadContext} />;
  };
};

// Hook to use reload in functional components with component remounting
export const useReloadableComponent = (componentName) => {
  const { reloadKey, softReload, isReloading } = useReload();
  
  const reload = useCallback(() => {
    console.log(`🔄 Reloading ${componentName} component`);
    softReload();
  }, [softReload, componentName]);

  return {
    key: `${componentName}-${reloadKey}`,
    reload,
    isReloading,
  };
};

// Component wrapper for easy reloading
export const ReloadableComponent = ({ 
  children, 
  componentName = 'ReloadableComponent',
  onReload = null,
  className = '',
  style = {}
}) => {
  const { key, reload, isReloading } = useReloadableComponent(componentName);

  const handleReload = useCallback(() => {
    if (onReload) {
      onReload();
    }
    reload();
  }, [onReload, reload]);

  return (
    <div 
      key={key} 
      className={`reloadable-component ${className}`}
      style={style}
    >
      {children}
      {isReloading && (
        <div className="reload-indicator">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Reloading...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Utility function to reload the entire application
export const reloadApplication = async (options = {}) => {
  const { forceHard = false, delay = 0 } = options;
  
  console.log('🔄 Reloading application...');
  
  return new Promise((resolve) => {
    const performReload = () => {
      if (forceHard) {
        window.location.reload();
      } else {
        // For soft reload, we dispatch a custom event that components can listen to
        window.dispatchEvent(new CustomEvent('app-reload'));
      }
      resolve(true);
    };

    if (delay > 0) {
      setTimeout(performReload, delay);
    } else {
      performReload();
    }
  });
};
